import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { themes, getStoredTheme, setStoredTheme, getTheme, applyTheme } from '../services/themeService.ts';
import './Header.css';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [currentTheme, setCurrentTheme] = useState<string>(getStoredTheme());
  const settingsRef = useRef<HTMLDivElement>(null);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query)}`);
      setQuery(''); // Clear search after navigation
    }
  };

  const toggleSettings = () => {
    setIsSettingsOpen(!isSettingsOpen);
  };

  // Close settings when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
        setIsSettingsOpen(false);
      }
    };

    if (isSettingsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSettingsOpen]);

  const handleThemeChange = (themeId: string) => {
    setCurrentTheme(themeId);
    setStoredTheme(themeId);
    const theme = getTheme(themeId);
    applyTheme(theme);
  };

  const currentThemeData = getTheme(currentTheme);

  return (
    <header className="header">
      <div className="container">
        <Link to="/" className="logo">
          {currentThemeData.headerImage && (
            <img 
              src={currentThemeData.headerImage} 
              alt="Pirate Bartender" 
              className="header-theme-image"
              onError={(e) => {
                if (process.env.NODE_ENV === 'development') {
                  console.error('Failed to load header image. Expected path:', currentThemeData.headerImage);
                  console.error('Make sure the image file exists at: public/images/pirate-bartender.png');
                }
                (e.target as HTMLImageElement).style.display = 'none';
              }}
              onLoad={() => {
                if (process.env.NODE_ENV === 'development') {
                  console.log('Header image loaded successfully from:', currentThemeData.headerImage);
                }
              }}
            />
          )}
          <div className="logo-content">
            <span className="logo-main">{currentThemeData.icon} Bobby's Bar Recipes</span>
            {currentTheme === 'drinks-ahoy' && (
              <span className="logo-edition">Drinks Ahoy! Edition</span>
            )}
            <span className="logo-subtitle">"Amazing bar recipes from around the world"</span>
          </div>
        </Link>
        <div className="header-right">
          <nav>
          <div className="settings-container" ref={settingsRef}>
            <button 
              className="settings-toggle" 
              onClick={toggleSettings}
              aria-label="Settings"
            >
              <span className="settings-icon">☰</span>
            </button>
            {isSettingsOpen && (
              <div className="settings-panel">
                <div className="settings-header">
                  <h3>Settings</h3>
                  <button 
                    className="settings-close" 
                    onClick={toggleSettings}
                    aria-label="Close settings"
                  >
                    ×
                  </button>
                </div>
                <div className="settings-content">
                  <div className="settings-section">
                    <h4 className="settings-section-title">Themes</h4>
                    <p className="settings-section-description">Choose a color scheme for the site</p>
                    <div className="theme-selector">
                      {themes.map((theme) => (
                        <button
                          key={theme.id}
                          className={`theme-option ${currentTheme === theme.id ? 'active' : ''}`}
                          onClick={() => handleThemeChange(theme.id)}
                          aria-label={`Select ${theme.name} theme`}
                        >
                          <span className="theme-icon">{theme.icon}</span>
                          <span className="theme-name">{theme.name}</span>
                          {currentTheme === theme.id && (
                            <span className="theme-check">✓</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
            <Link to="/" className="nav-link">Home</Link>
            <Link to="/browse" className="nav-link">Browse</Link>
            <Link to="/favorites" className="nav-link">Favorites</Link>
          </nav>
          <form onSubmit={handleSearch} className="header-search-form">
            <div className="header-search-container">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="e.g., Margarita, Vodka, Lime..."
                className="header-search-input"
              />
              <button type="submit" className="header-search-btn">
                🔍
              </button>
            </div>
          </form>
        </div>
      </div>
    </header>
  );
};

export default Header;